"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn, dayjs } from "utils";
import { useCallback, useMemo, useRef } from "react";

import log from "logbeacon";
import { useQueryClient } from "@tanstack/react-query";
import { TrackerReportItemLoading } from "./TimeLineItem/TrackerReportItemLoading";
import { I18nUtil } from "@utils/i18n";
import { Markdown } from "@/src/components/Markdown";
import { QUERY_KEYS } from "@/src/config/constant";
import Translations from "@translate/components/CoPilot/CopilotAgent/SummaryReportTip";
import { useScrollManager } from "@/src/hooks/useScrollManager";
import { useSummaryStream } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

export const SummaryReportTip = ({
  id,
  agentId,
  content,
  isOpen = false,
  onOpenChange,
  onChatClick,
  created,
}: {
  id?: string;
  isOpen?: boolean;
  agentId?: string;
  created?: number;
  content?: {
    summary: string;
    title: string;
  };
  onOpenChange?: (open: boolean) => void;
  onChatClick?: (value: {
    question?: string;
    title?: string;
    id?: string;
    desc?: string;
    type?: string;
  }) => void;
}) => {
  const queryClient = useQueryClient();
  const { getAiSummary, isLoading, summary } = useSummaryStream();
  const summaryScrollRef = useRef<HTMLDivElement | null>(null);

  const { setIsScrollingUp, setdisabled } = useScrollManager({
    topOffset: 0,
    scrollContainerRef: summaryScrollRef,
  });

  const openAutoScroll = useCallback(() => {
    setdisabled(false);
    setIsScrollingUp(false);
  }, [setIsScrollingUp, setdisabled]);

  const hasSummary = useMemo(
    () => !!(summary || content?.summary),
    [summary, content?.summary],
  );

  return (
    <div
      data-openreplay-label="CopilotAgent · SummaryReportTip · div"
      className={cn("rounded-3 bg-background_solid_tertiary cursor-pointer", {
        "p-4": !isLoading,
      })}
      onClick={(e) => {
        e.stopPropagation();
        // 点击整个卡片区域，只能展开，不能收起
        if (!isOpen) {
          onOpenChange?.(true);
        }
      }}
    >
      {isLoading && (
        <TrackerReportItemLoading
          text={I18n.t(
            "Preparing your market summary, simple, sharp, ready to execute",
          )}
        />
      )}
      {!isLoading && (
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Icon
                name="portfolio_solid"
                size={14}
                color={ospThemes.colors.content_brand_primary}
              />
              <div className="body_bold_s text-content_brand_primary">
                {I18n.t("Daily Market Summary")}
              </div>
              {hasSummary && (
                <div className="body_s text-content_tertiary">
                  {dayjs(created).format("HH:mm")}
                </div>
              )}
            </div>
            {!hasSummary && (
              <div className="title_l text-content_primary mt-0.5">
                {I18n.t(
                  "All today's alerts summarized and clarified to save your time.",
                )}
              </div>
            )}
          </div>
          {!isLoading && !hasSummary && (
            <button
              aria-label="Summary Report Tip"
              data-openreplay-label="Summary Report Tip Button"
              id="btn-summary-report-tip"
              className="button sm_button primary_button"
              onClick={async () => {
                onOpenChange?.(true);
                openAutoScroll();
                const result = await getAiSummary({
                  agentId: agentId || "",
                  type: "SUMMARY_NEWS",
                });
                if (result) {
                  setTimeout(() => {
                    localStorage.setItem("is_summary_loaded", agentId || "");
                    queryClient.invalidateQueries({
                      queryKey: [QUERY_KEYS.AGENT_REPORT_LIST],
                    });
                  }, 1000);
                }
              }}
            >
              <span className="button_m text-content_inverse_primary">
                {I18n.t("Generate")}
              </span>
            </button>
          )}
          {!isLoading && hasSummary && (
            <div className="flex items-center">
              {content?.summary && (
                <button
                  aria-label="Summary Report Tip"
                  data-openreplay-label="Summary Report Tip Button"
                  id="btn-summary-report-tip-2"
                  className="button xs_button button_icon text-content_primary flex items-center gap-1"
                  onClick={(e) => {
                    log.debug("[文案]onChatClick", id);
                    e.stopPropagation();
                    e.preventDefault();
                    onChatClick?.({
                      title: I18n.t("Daily Market Summary"),
                      id: id,
                      question: I18n.t("Give me more info about this report"),
                      desc: "",
                      type: "SUMMARY",
                    });
                  }}
                >
                  <Icon
                    name="p_chat"
                    size={16}
                    color={ospThemes.colors.content_primary}
                  />

                  <span className="button_s">{I18n.t("Ask")}</span>
                </button>
              )}

              <span className="border-r border-border_solid h-4 pl-1 mr-1" />
              <Icon
                data-openreplay-label="chevron-down"
                name={"chevron-down"}
                size={20}
                color={ospThemes.colors.content_primary}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenChange?.(!isOpen);
                }}
                className={cn(
                  "transition-transform duration-200 cursor-pointer button_icon px-[2px]",
                  isOpen && "rotate-180",
                )}
              />
            </div>
          )}
        </div>
      )}

      {!isLoading && hasSummary && content?.summary && (
        <div
          className={cn(
            "body_m text-content_secondary h-[152px] overflow-hidden transition-all duration-200",
            isOpen && "h-fit overflow-auto",
          )}
        >
          <Markdown className="[&_a]:!body_output [&_div]:!body_output_m [&_h1]:!mb-0 [&_h2]:!mb-0 [&_h3]:!mb-0 [&_p]:!mt-3 [&_p]:!mb-0 [&_p]:!body_output_m [&_li]:!body_output_m [&_span]:!body_medium_m [&_li]:!pt-2 [&_li]:!pb-0 [&_td_div]:!line-height-[20px]">
            {content?.summary}
          </Markdown>
        </div>
      )}
      <div className="relative">
        <div
          className={cn(
            "body_m text-content_secondary overflow-y-auto no-scrollbar",
            isOpen && "max-h-[380px]",
          )}
          ref={summaryScrollRef}
        >
          {!isLoading && summary && (
            <div className={cn("h-[140px]", isOpen && "h-[380px]")}>
              <Markdown className="[&_a]:!body_output [&_div]:!body_output_m [&_h1]:!mb-0 [&_h2]:!mb-0 [&_h3]:!mb-0 [&_p]:!mt-3 [&_p]:!mb-0 [&_p]:!body_output_m [&_li]:!body_output_m [&_span]:!body_medium_m [&_li]:!pt-2 [&_li]:!pb-0 [&_td_div]:!line-height-[20px]">
                {summary}
              </Markdown>
            </div>
          )}
        </div>
        {!isOpen && !isLoading && (
          <div
            className="absolute md:bottom-[-16px] bottom-[-12px] left-0 w-full h-[58px] pointer-events-none"
            style={{
              background:
                "linear-gradient(0deg, var(--edgen-background_solid_tertiary) 0%, rgba(37, 37, 37, 0.00) 77.59%)",
            }}
          />
        )}
      </div>
    </div>
  );
};
